/** Typing indicators: entries from realtime `typing` events, expiry, client-side send throttle, label. */
import { msg, type MessageDescriptor } from "../i18n.ts";

export type TypingEntry = { userId: string; channelId: string; threadRootId?: string; at: number };

/** A typing event is shown for this long unless refreshed (clients send at most one every 3 s). */
export const TYPING_TTL_MS = 6000;
export const TYPING_SEND_INTERVAL_MS = 3000;

function sameScope(e: TypingEntry, channelId: string, threadRootId: string | undefined): boolean {
  return e.channelId === channelId && (e.threadRootId ?? "") === (threadRootId ?? "");
}

/** Records (or refreshes) that `userId` is typing; also drops entries older than the TTL. */
export function addTyping(
  entries: TypingEntry[],
  userId: string,
  channelId: string,
  threadRootId: string | undefined,
  now: number,
): TypingEntry[] {
  const kept = entries.filter(
    (e) => now - e.at < TYPING_TTL_MS && !(e.userId === userId && sameScope(e, channelId, threadRootId)),
  );
  const entry: TypingEntry =
    threadRootId === undefined ? { userId, channelId, at: now } : { userId, channelId, threadRootId, at: now };
  return [...kept, entry];
}

/** Clears a user's typing in a channel (e.g. when their message arrives), in every thread scope. */
export function clearTyping(entries: TypingEntry[], userId: string, channelId: string): TypingEntry[] {
  return entries.filter((e) => !(e.userId === userId && e.channelId === channelId));
}

/** User ids typing in this channel/thread, oldest first, excluding me and expired entries. */
export function activeTypers(
  entries: TypingEntry[],
  channelId: string,
  threadRootId: string | undefined,
  meId: string,
  now: number,
): string[] {
  const out: string[] = [];
  for (const e of entries) {
    if (e.userId === meId || !sameScope(e, channelId, threadRootId)) continue;
    if (now - e.at >= TYPING_TTL_MS || now < e.at - TYPING_TTL_MS) continue;
    if (!out.includes(e.userId)) out.push(e.userId);
  }
  return out;
}

/** Earliest time at which one of the active entries expires (to schedule a re-render), or undefined. */
export function nextTypingExpiry(entries: TypingEntry[], now: number): number | undefined {
  let next: number | undefined;
  for (const e of entries) {
    const exp = e.at + TYPING_TTL_MS;
    if (exp > now && (next === undefined || exp < next)) next = exp;
  }
  return next;
}

/** "Ada is typing…", "Ada and Bo are typing…", "Several people are typing…", or undefined for nobody. */
export function typingLabel(names: string[]): MessageDescriptor | undefined {
  const a = names[0];
  const b = names[1];
  if (a === undefined) return undefined;
  if (b === undefined) return msg("typing.one", { name: a });
  if (names.length === 2) return msg("typing.two", { a, b });
  return msg("typing.many");
}

/** Throttle for outgoing typing frames: send if never sent or the interval has elapsed. */
export function shouldSendTyping(lastSentAt: number | undefined, now: number): boolean {
  return lastSentAt === undefined || now - lastSentAt >= TYPING_SEND_INTERVAL_MS || now < lastSentAt;
}
